import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import {
  getLogoForAccount,
  getTradingAccountIcon,
} from '@pages/Trade/TradingAccountInfo/utils/getLogoForAccount'

import { BaseModalType, useModal } from '@context/ModalsContext'

import { Body2 } from '@ui/Typography'
import { IconButton } from '@ui/components/Button'
import { ListItem } from '@ui/components/List'
import { Modal } from '@ui/components/Modal'
import { Popover, PopoverCard, PopoverList } from '@ui/components/Popover'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { ArrowLeftRightIcon } from '@ui/icons/ArrowLeftRight.icon'
import { PencilIcon } from '@ui/icons/Pencil.icon'
import { SettingIcon } from '@ui/icons/Setting.icon'
import { WarningIcon } from '@ui/icons/Warning.icon'
import { IconFCType } from '@ui/icons/types'

import { useWindowResize } from '@utils/domUtils'
import { TradingAccountRestrictions, isRestricted } from '@utils/trading-account.utils'

import { NameDto } from '@model/NameDto'
import { isTMTPlatformType } from '@model/PlatformTypeEnum'
import { DemoTradingAccount, TradingAccount } from '@model/TradingAccount'

import { Box } from '@shared/components/Box'

import { ChangeNicknameModal } from '../modals/ChangeNickname.modal'

import styles from './TradingAccountActionsDropdown.module.scss'

type MenuSection = 'change' | 'other'

type MobileItem = {
  label: string
  disabled: boolean
  onRestricted: () => void
  onAllowed: () => void
  icon?: IconFCType
  section?: MenuSection
}

interface ActionListItemProps {
  label: string
  disabled: boolean
  restrictedNote?: string
  icon?: IconFCType
  onClick: () => void
  variant?: 'mobile' | 'desktop'
}

const ActionListItem = React.memo<ActionListItemProps>(
  ({ label, disabled, restrictedNote, icon = PencilIcon, onClick, variant = 'desktop' }) => {
    return (
      <ListItem
        onClick={onClick}
        startSlot={{ icon }}
        disabled={disabled}
        size={variant === 'mobile' ? 'lg' : 'md'}
        subtitle={disabled && restrictedNote ? restrictedNote : undefined}
        endSlot={
          disabled
            ? { icon: { icon: { icon: WarningIcon, color: 'text-danger-high-em' } } }
            : undefined
        }
      >
        {label}
      </ListItem>
    )
  }
)

export const TradingAccountActionsMobileModal: BaseModalType<{
  title: string
  items: MobileItem[]
  restrictedNote: string
  tradingAccount: TradingAccount | DemoTradingAccount
  platformLogo: IconFCType
}> = ({ close, title, items, restrictedNote, platformLogo: PlatformLogo }) => {
  const { t } = useTranslation()

  const { changeItems, otherItems } = useMemo(
    () => ({
      changeItems: items.filter((item) => item.section === 'change'),
      otherItems: items.filter((item) => item.section === 'other'),
    }),
    [items]
  )

  const handleItemClick = useCallback(
    (item: MobileItem) => () => {
      if (item.disabled) item.onRestricted()
      else item.onAllowed()
      close()
    },
    [close]
  )

  return (
    <Modal>
      <Modal.Header icon={PlatformLogo}>{title}</Modal.Header>
      <Modal.Body onClick={(e) => e.stopPropagation()}>
        <PopoverList>
          {otherItems.map((it, idx) => (
            <ActionListItem
              key={`other-${idx}`}
              label={it.label}
              disabled={it.disabled}
              restrictedNote={restrictedNote}
              icon={it.icon || ArrowLeftRightIcon}
              onClick={handleItemClick(it)}
              variant='mobile'
            />
          ))}

          {changeItems.length > 0 && (
            <>
              <Box p='x8' className={styles.bordered}>
                <Body2 semibold color='text-med-em'>
                  {t('Change')}
                </Body2>
              </Box>
              {changeItems.map((it, idx) => (
                <ActionListItem
                  key={`change-${idx}`}
                  label={it.label}
                  disabled={it.disabled}
                  restrictedNote={restrictedNote}
                  icon={it.icon}
                  onClick={handleItemClick(it)}
                  variant='mobile'
                />
              ))}
            </>
          )}
        </PopoverList>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary onClick={close}>Close</Modal.Footer.Secondary>
      </Modal.Footer>
    </Modal>
  )
}

interface Props {
  tradingAccount: TradingAccount | DemoTradingAccount
  restrictions: NameDto[]
  onChangePasswordRestrictedClick: () => void
  onChangeInvestorPasswordRestrictedClick: () => void
  isTransferFromRestricted?: boolean
  onTransferRestrictedClick?: () => void
  onTransferClick?: () => void
  onResetPasswordClick: () => void
  isDemo?: boolean
  onResetPasswordRestrictedClick: () => void
}

export const TradingAccountActionsDropdown: React.FC<Props> = ({
  tradingAccount,
  restrictions,
  onChangePasswordRestrictedClick,
  onChangeInvestorPasswordRestrictedClick,
  isTransferFromRestricted = false,
  onTransferRestrictedClick,
  onTransferClick,
  isDemo = false,
}) => {
  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)
  const setOpen = useCallback((v: boolean) => {
    setIsDropdownOpen(v)
  }, [])

  const navigate = useNavigate()

  const isTMT =
    isTMTPlatformType(tradingAccount.platformType.id) ||
    isTMTPlatformType(tradingAccount?.parentPlatformType?.id)

  const hasChangeMasterPasswordRestriction = isRestricted(
    TradingAccountRestrictions.CHANGE_MASTER_PASSWORD,
    restrictions
  )
  const hasChangeInvestorPasswordRestriction = isRestricted(
    TradingAccountRestrictions.CHANGE_INVESTOR_PASSWORD,
    restrictions
  )

  const labels = useMemo(
    () => ({
      changePassword: isTMT
        ? t('Trading Account.Change user id password')
        : t('Trading Account.Change Trading Account Password'),
    }),
    [isTMT, t]
  )

  const mobileModal = useModal(TradingAccountActionsMobileModal)
  const changeNicknameModal = useModal(ChangeNicknameModal)

  const handleChangeNickname = useCallback(() => {
    changeNicknameModal.open({
      tradingAccountId: tradingAccount.id,
      currentNickname: tradingAccount.nickname ?? '',
      isDemo,
    })
    setOpen(false)
  }, [changeNicknameModal, tradingAccount.id, tradingAccount.nickname, isDemo])

  const mobileItems = useMemo<MobileItem[]>(() => {
    const items: MobileItem[] = [
      {
        label: labels.changePassword,
        disabled: hasChangeMasterPasswordRestriction,
        onRestricted: onChangePasswordRestrictedClick,
        onAllowed: () =>
          navigate(
            isTMT
              ? `/dashboard/traders-room/trading-accounts/${tradingAccount.id}/change-password?changeTickmillTraderPassword=true`
              : `/dashboard/traders-room/trading-accounts/${tradingAccount.id}/change-password?changeInvestorPassword=false`
          ),
        section: 'change',
      },
    ]

    if (!isTMT) {
      items.push({
        label: t('Trading Account.Change Investor Password'),
        disabled: hasChangeInvestorPasswordRestriction,
        onRestricted: onChangeInvestorPasswordRestrictedClick,
        onAllowed: () =>
          navigate(
            `/dashboard/traders-room/trading-accounts/${tradingAccount.id}/change-password?changeInvestorPassword=true`
          ),
        section: 'change',
      })
    }

    if (onTransferClick) {
      items.push({
        label: t('Trading Account.Transfer Funds To Wallet'),
        disabled: isTransferFromRestricted,
        onRestricted: () => onTransferRestrictedClick?.(),
        onAllowed: onTransferClick,
        icon: ArrowLeftRightIcon,
        section: 'other',
      })
    }

    items.push({
      label: t('Trading Account.Nickname'),
      disabled: false,
      onRestricted: () => {},
      onAllowed: () => {
        changeNicknameModal.open({
          tradingAccountId: tradingAccount.id,
          currentNickname: tradingAccount.nickname ?? '',
          isDemo,
        })
      },
      section: 'change',
    })

    return items
  }, [
    labels,
    hasChangeMasterPasswordRestriction,
    hasChangeInvestorPasswordRestriction,
    onChangePasswordRestrictedClick,
    onChangeInvestorPasswordRestrictedClick,
    onTransferRestrictedClick,
    onTransferClick,
    navigate,
    isTMT,
    tradingAccount.id,
    t,
    isTransferFromRestricted,
  ])
  const title = tradingAccount.nickname || tradingAccount.name

  const openMobile = useCallback(() => {
    mobileModal.open({
      title,
      items: mobileItems,
      restrictedNote: t('(Restricted)'),
      tradingAccount,
      platformLogo: getTradingAccountIcon(tradingAccount.platformType.id),
    })
  }, [mobileModal, mobileItems, t, tradingAccount])

  const createHandler =
    (
      isRestricted: boolean,
      restrictedCallback: (() => void) | undefined,
      allowedCallback: () => void
    ) =>
    () => {
      if (isRestricted) {
        restrictedCallback?.()
      } else {
        allowedCallback()
      }
      setOpen(false)
    }

  const handleChangePassword = createHandler(
    hasChangeMasterPasswordRestriction,
    onChangePasswordRestrictedClick,
    () =>
      navigate(
        `/dashboard/traders-room/trading-accounts/${tradingAccount.id}/change-password?changeInvestorPassword=false`
      )
  )

  const handleChangeTMTPassword = createHandler(
    hasChangeMasterPasswordRestriction,
    onChangePasswordRestrictedClick,
    () =>
      navigate(
        `/dashboard/traders-room/trading-accounts/${tradingAccount.id}/change-password?changeTickmillTraderPassword=true`
      )
  )
  const handleChangeInvestorPassword = createHandler(
    hasChangeInvestorPasswordRestriction,
    onChangeInvestorPasswordRestrictedClick,
    () =>
      navigate(
        `/dashboard/traders-room/trading-accounts/${tradingAccount.id}/change-password?changeInvestorPassword=true`
      )
  )

  const handleTransferToWallet = () => {
    if (onTransferClick) {
      if (isTransferFromRestricted) {
        onTransferRestrictedClick?.()
      } else {
        onTransferClick()
      }
      setOpen(false)
    }
  }

  const isTransferDisabled = isTransferFromRestricted || !onTransferClick

  if (isMobile) {
    return (
      <IconButton.Secondary
        size='md'
        icon={SettingIcon}
        onClick={(e) => {
          e.stopPropagation()
          openMobile()
        }}
      />
    )
  }

  return (
    <Popover
      direction='topRight'
      distance='0.5rem'
      label='Password Actions'
      isOpen={isDropdownOpen}
      setIsOpen={setOpen}
      content={
        <PopoverCard>
          <PopoverList>
            {
              <Box flex gap='x12' align='center' content='row' p='x8'>
                <Box flex align='center' content='center' className={styles.logoCircle}>
                  {getLogoForAccount(tradingAccount, 24)}
                </Box>
                <Body2>{tradingAccount.nickname || tradingAccount.name}</Body2>
              </Box>
            }

            {onTransferClick && (
              <Tooltip
                content={t('This action is currently unavailable.')}
                disabled={!isTransferDisabled}
              >
                <ActionListItem
                  label={t('Trading Account.Transfer Funds To Wallet')}
                  disabled={isTransferDisabled}
                  restrictedNote={t('(Restricted)')}
                  icon={ArrowLeftRightIcon}
                  onClick={handleTransferToWallet}
                />
              </Tooltip>
            )}

            <Box className={styles.bordered} p='x8' pt='x12'>
              <Body2 semibold color='text-med-em'>
                {t('Change')}
              </Body2>
            </Box>
            <Tooltip
              content={t('This action is currently unavailable.')}
              disabled={!hasChangeMasterPasswordRestriction}
            >
              <ActionListItem
                label={labels.changePassword}
                disabled={hasChangeMasterPasswordRestriction}
                restrictedNote={t('(Restricted)')}
                onClick={isTMT ? handleChangeTMTPassword : handleChangePassword}
              />
            </Tooltip>

            {!isTMT && (
              <Tooltip
                content={t('This action is currently unavailable.')}
                disabled={!hasChangeInvestorPasswordRestriction}
              >
                <ActionListItem
                  label={t('Trading Account.Change Investor Password')}
                  disabled={hasChangeInvestorPasswordRestriction}
                  restrictedNote={t('(Restricted)')}
                  onClick={handleChangeInvestorPassword}
                />
              </Tooltip>
            )}

            <ActionListItem
              label={t('Trading Account.Nickname')}
              disabled={false}
              onClick={handleChangeNickname}
            />
          </PopoverList>
        </PopoverCard>
      }
    >
      <IconButton.Secondary
        size='md'
        icon={SettingIcon}
        onClick={(e) => {
          e.stopPropagation()
          setOpen(true)
        }}
      />
    </Popover>
  )
}
